import { createBrowserRouter, RouterProvider } from 'react-router';
import './index.css';
import Device from './routes/device/Device';
import RootLayout from './layouts/RootLayout';
import Home from './routes/home/Home';
import { ThemeProvider } from './components/theme/ThemeProvider';
import { loader as measurementGroupLoader } from './routes/measurement-group/constants';
import { loader as deviceLoader } from './routes/device/constants';
import { measurementGroupListLoader } from './routes/measurement-group//list/constants';
import { MeasurementGroups } from './routes/measurement-group/list/MeasurementGroups';
import { MeasurementGroup } from '@/routes/measurement-group/MeasurementGroup';
import CalibrationTask from './routes/calibration-task/CalibrationTask';
import { calibrationTaskLoader } from './routes/calibration-task/constants';
import RouteErrorPage from './components/route-error-page/RouteErrorPage';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CalibrationTasks from './routes/calibration-task/list/CalibrationTasks';
import { calibrationTasksListLoader } from './routes/calibration-task/list/constants';
import HydrateFallbackSpinner from './components/hydrate-fallback/HydrateFallbackSpinner';
import { ROUTES } from './constants';
import LoginPage from './components/auth/LoginPage';
import { withAuth } from './components/auth/withAuthLoader';
import useAuthInject from './components/auth/useAuthInject';
import { getAccessTokenSilentlyRef } from './api';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ProcessObjects } from './routes/process-objects/list/ProcessObjects';
import { processObjectListLoader } from './routes/process-objects/list/constants';
import Landowner from '@/routes/landowner/Landowner.tsx';
import { loader as siteLoader } from '@/routes/landowner/constants.ts';
import { DeployDevice } from './routes/device/deploy-device/DeployDevice';
import { deployDeviceLoader } from './routes/device/deploy-device/constants';
import GpsLock from '@/routes/device/gps-lock/GpsLock.tsx';
import Map from './routes/map/Map';
import { FieldReportsList } from '@/routes/field-reports-list/FieldReportsList.tsx';
import { loader as fieldReportsListLoader } from '@/routes/field-reports-list/constants.ts';
import { useAuth0 } from '@auth0/auth0-react';
import { filterRoutesByRoles } from '@/utils/routesFilteringByRoles.ts';
import { Role, useRoles } from '@/hooks/useRoles.ts';

const queryClient = new QueryClient({
	mutationCache: new MutationCache({
		onSuccess: async (_data, _variables, _context, mutation) => {
			await queryClient.invalidateQueries({
				queryKey: mutation.options.mutationKey
			});
		}
	}),
	defaultOptions: {
		queries: {
			retry: false
		}
	}
});

function App() {
	useAuthInject();

	const userRoles = useRoles();
	const { isAuthenticated } = useAuth0();

	const routes = [
		{
			index: true,
			element: <Home />
		},
		{
			path: ROUTES.login,
			element: <LoginPage />
		},
		{
			path: ROUTES.getDeviceURL(),
			element: <Device />,
			loader: withAuth(deviceLoader(queryClient)),
			errorElement: <RouteErrorPage />,
			requiredRoles: [Role.ROLE_ANALYST]
		},
		{
			path: ROUTES.deployDevice,
			element: <DeployDevice />,
			loader: withAuth(deployDeviceLoader(queryClient)),
			errorElement: <RouteErrorPage />,
			requiredRoles: [Role.ROLE_ANALYST]
		},
		{
			path: ROUTES.gpsLockDevice,
			element: <GpsLock />,
			errorElement: <RouteErrorPage />,
			requiredRoles: [Role.ROLE_CONTRACTOR]
		},
		{
			path: ROUTES.tasks,
			element: <CalibrationTasks />,
			loader: withAuth(calibrationTasksListLoader(queryClient)),
			errorElement: <RouteErrorPage />,
			requiredRoles: [Role.ROLE_ANALYST]
		},
		{
			path: ROUTES.measurementGroups,
			children: [
				{
					index: true,
					element: <MeasurementGroups />,
					loader: withAuth(measurementGroupListLoader(queryClient)),
					errorElement: <RouteErrorPage />
				},
				{
					path: ROUTES.getMeasurementGroupURL(),
					children: [
						{
							index: true,
							element: <MeasurementGroup />,
							loader: withAuth(measurementGroupLoader(queryClient)),
							errorElement: <RouteErrorPage />
						},
						{
							path: ROUTES.getMeasurementGroupCalibrationUrl(),
							element: <CalibrationTask />,
							loader: withAuth(calibrationTaskLoader(queryClient)),
							errorElement: <RouteErrorPage />
						}
					]
				}
			],
			requiredRoles: [Role.ROLE_ANALYST]
		},
		{
			path: ROUTES.processObjects,
			element: <ProcessObjects />,
			loader: withAuth(processObjectListLoader(queryClient)),
			errorElement: <RouteErrorPage />,
			requiredRoles: [Role.ROLE_ANALYST]
		},
		{
			path: ROUTES.landowner,
			element: <Landowner />,
			loader: withAuth(siteLoader(queryClient)),
			errorElement: <RouteErrorPage />,
			requiredRoles: [Role.ROLE_ANALYST]
		},
		{
			path: ROUTES.map,
			element: <Map />,
			loader: withAuth(() => null),
			errorElement: <RouteErrorPage />,
			requiredRoles: [Role.ROLE_ANALYST]
		},
		{
			path: ROUTES.fieldReports,
			element: <FieldReportsList />,
			loader: withAuth(fieldReportsListLoader(queryClient)),
			errorElement: <RouteErrorPage />,
			requiredRoles: [Role.ROLE_ANALYST]
		}
	];

	const router = createBrowserRouter([
		{
			path: ROUTES.home,
			element: <RootLayout />,
			errorElement: <RouteErrorPage />,
			hydrateFallbackElement: <HydrateFallbackSpinner />,
			children: isAuthenticated ? filterRoutesByRoles(routes, userRoles) : routes
		}
	]);

	if (!getAccessTokenSilentlyRef) {
		return null;
	}

	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
				<RouterProvider router={router} />
			</ThemeProvider>
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>
	);
}

export default App;
